<template>
    <UApp :locale="uiLocale">
        <div v-if="booting" class="flex min-h-screen items-center justify-center bg-default">
            <div class="flex flex-col items-center gap-4">
                <AtomLogo class="size-12 animate-pulse" />
                <UIcon name="i-lucide-loader-circle" class="size-6 animate-spin text-primary" />
            </div>
        </div>
        <component :is="layout" v-else>
            <router-view />
        </component>
    </UApp>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref } from 'vue'
    import { useRoute, useRouter } from 'vue-router'
    import { useI18n } from 'vue-i18n'
    import * as locales from '@nuxt/ui/locale'

    import Default from '@/layouts/Default.vue'
    import Blank   from '@/layouts/Blank.vue'
    import AtomLogo from '@/layouts/components/AtomLogo.vue'

    import { useAppStore }   from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import { reporterMayOpen } from '@/router'
    import { UserRole } from '@/enums/domain'
    import type { IUser } from '@/types/User'

    const router = useRouter()
    const route = useRoute()
    const { locale } = useI18n()
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const booting = ref(true)

    const uiLocale = computed(() => (locales as Record<string, typeof locales.en>)[locale.value] ?? locales.en)

    const layouts = { Default, Blank }
    const layout  = computed(() => layouts[(route.meta.layout as keyof typeof layouts) ?? 'Default'])

    onMounted(async () => {
        if (appStore.isLoggedIn()) {
            const { statusCode, data } = await fetchStore.fetch('/me').get().json<IUser>()
            if (statusCode.value === 200 && data.value) {
                appStore.setUser(data.value)
                // The route guard ran before the user was known: the reporter role is
                // confined to Reports and personal pages (v1.16).
                if (data.value.role === UserRole.Reporter && !reporterMayOpen(route.name)) {
                    router.replace({ name: 'reports' })
                }
            } else {
                appStore.setLogout()
            }
        }
        booting.value = false
    })
</script>
